import { ColDef, PostSortRowsParams, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useRef } from 'react';
import { aggridHeaderClass } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';
import {
	createCellCssClassRules,
	createGridDropDownCommitAfter,
	CreateCellClickHandler,
	CustomCellClickAction,
	GridCellConfig,
	InsertRowHandler,
	postSortHandler,
	RemoveRowHandler,
	useCellChangeHandler,
} from 'src/utils/grid/GridHandlerContent';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { CommonCodeColumn } from 'src/utils/grid/GridColumnContent';
import { useDetailTableEvents } from 'src/hooks/detail-table/useDetailTableEvents';
import { TQC5010_R01Dto } from './Dto_TQC5010_R01';
import { useTQC5010_R01Store } from './Store_TQC5010_R01';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";

const COLUMN_STATE_KEY = 'TQC5010_R01-grid-column-state';

const defaultEditableColumns = [
    'inspItemGroup', 'inspType', 'inspItemNm', 'specValue1', 'specRange',
    'specValue2', 'inspUnit', 'valueType', 'inspMethod', 'remark',
];

const headerNames: Record<string, string> = {
    rowIndex: '',
    inspItemGroup: '검사항목그룹',
    inspType: '검사구분',
    inspItemCd: '검사기준코드',
    inspItemNm: '검사기준명',
    specValue1: '검사값1',
    specRange: '검사범위구분',
    specValue2: '검사값2',
    inspUnit: '검사단위',
    valueType: '검사값유형',
    inspMethod: '검사방법',
    remark: '비고',
};

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<TQC5010_R01Dto> | null>;
	defaultTQC5010_R01Row: TQC5010_R01Dto;
}

export const Table_TQC5010_R01 = ({
	clickable,
	setClickable,
	gridRef,
	defaultTQC5010_R01Row,
}: Props) => {
	const {
		TQC5010_R01List, setTQC5010_R01List,
		insertTQC5010_R01List, removeTQC5010_R01List, updateTQC5010_R01List,
		deletedTQC5010_R01List, addDeletedTQC5010_R01List,
		selectedTQC5010_R01List, addSelectedTQC5010_R01List, removeSelectedTQC5010_R01List, setSelectedTQC5010_R01List,
		initSelectedTQC5010_R01List, allSelectedTQC5010_R01List,
		originalTQC5010_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useTQC5010_R01Store();
	const { itemGroupList, inspectionTypeList, inspectionRangeList, inspectionValueList, inspectMethodList, unitList } = useCommonCodeStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: TQC5010_R01List,
		defaultGridData: defaultTQC5010_R01Row,
		selectedRow: selectedTQC5010_R01List,
		originalGridData: originalTQC5010_R01List,
		updateRow: (index: number, data: any) => updateTQC5010_R01List(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['inspItemCd'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['inspItemGroup', 'inspType'],

		pointerCells: ['inspItemNm', 'specValue1', 'specRange', 'specValue2', 'inspUnit', 'valueType', 'inspMethod', 'remark'],
		insertedPointerCells: ['inspItemGroup', 'inspType'],
		commonPointerCells: [''],

		alignLeftCells: ['inspItemNm', 'remark'],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<TQC5010_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedTQC5010_R01List,
			deletedList: deletedTQC5010_R01List,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		selectedTQC5010_R01List , createCellClassRules, deletedTQC5010_R01List,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns, alignLeftColumns,
	]);


	const handleGridDropDownCommitAfter = useMemo(
		() => createGridDropDownCommitAfter<TQC5010_R01Dto>(defaultEditableColumns),
		[],
	);

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: TQC5010_R01List,
			rowData: TQC5010_R01List[rowIndex],
			insertRow: insertTQC5010_R01List,
			defaultRowData: defaultTQC5010_R01Row,
			setClickable,
		});
	}, [TQC5010_R01List, insertTQC5010_R01List, defaultTQC5010_R01Row, setClickable]);

	/** 삭제 시 deletedList에 valueIndex를 추가하는 콜백 */
	const handleAfterRemove = useCallback((ids: string[]) => {
		addDeletedTQC5010_R01List(ids);
	}, [addDeletedTQC5010_R01List]);

	/** 행 삭제 버튼 클릭 핸들러 */
	const removeRowHandler = useCallback((rowData: TQC5010_R01Dto) => {
		RemoveRowHandler(
			gridRef,
			TQC5010_R01List,
			rowData,
			setClickable,
			removeTQC5010_R01List,
			handleAfterRemove,
		);
	}, [gridRef, TQC5010_R01List, setClickable, removeTQC5010_R01List, handleAfterRemove]);

	/** 행 인덱스 셀의 스타일 플래그 계산 */
	const rowStyleFlagsHandler = useCallback((innerParams: Parameters<typeof getRowStyleFlags>[0]) => (
		getRowStyleFlags(innerParams, {
			deletedList: deletedTQC5010_R01List,
			selectedList: selectedTQC5010_R01List,
			dataList: TQC5010_R01List,
		})
	), [deletedTQC5010_R01List, selectedTQC5010_R01List, TQC5010_R01List]);

	/** + 버튼 클릭(Ctrl 다중 삽입 포함) 핸들러 */
	const insertRowClickHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		if (e.ctrlKey) {
			for (let i = 0; i < 10; i++) insertRowHandler(rowIndex, e);
		} else {
			insertRowHandler(rowIndex, e);
		}
	}, [insertRowHandler]);

	/** 인덱스 컬럼 렌더링 핸들러 */
	const renderIndexCellHandler = useCallback((params: Parameters<typeof renderIndexCell>[0]) => (
		renderIndexCell(params, rowStyleFlagsHandler, insertRowClickHandler, removeRowHandler)
	), [renderIndexCell, rowStyleFlagsHandler, insertRowClickHandler, removeRowHandler]);

	const baseColumnDefs: ColDef<TQC5010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(renderIndexCellHandler),
		{
			headerName: '품목그룹', colId: 'inspItemGroup', field: 'inspItemGroup', width: 110,
			...CommonCodeColumn.options(
				itemGroupList,
				(params) => params.data?.inserted === true,
				CommonCodeColumn.change(updateTQC5010_R01List),
				handleGridDropDownCommitAfter,
			),
		},
		{
			headerName: '검사구분', colId: 'inspType', field: 'inspType', width: 100,
			...CommonCodeColumn.options(
				inspectionTypeList,
				(params) => params.data?.inserted === true,
				CommonCodeColumn.change(updateTQC5010_R01List),
				handleGridDropDownCommitAfter,
			),
		},
		{
			headerName: '항목코드', colId: 'inspItemCd', field: 'inspItemCd', width: 100,
			editable: false,
			pasteable: false,
		},
		{
			headerName: '항목명', colId: 'inspItemNm', field: 'inspItemNm', width: 200,
			editable: true,
			pasteable: true,
		},
		{
			headerName: '검사값1', colId: 'specValue1', field: 'specValue1', width: 80,
			editable: true,
			pasteable: true,
		},
		{
			headerName: '범위', colId: 'specRange', field: 'specRange', width: 70,
			...CommonCodeColumn.options(
				inspectionRangeList, true,
				CommonCodeColumn.change(updateTQC5010_R01List), handleGridDropDownCommitAfter,
			),
		},
		{
			headerName: '검사값2', colId: 'specValue2', field: 'specValue2', width: 80,
			editable: true,
			pasteable: true,
		},
		{
			headerName: '단위', colId: 'inspUnit', field: 'inspUnit', width: 70,
			...CommonCodeColumn.options(
				unitList, true,
				CommonCodeColumn.change(updateTQC5010_R01List), handleGridDropDownCommitAfter,
			),
		},
		{
			headerName: '값유형', colId: 'valueType', field: 'valueType', width: 90,
			...CommonCodeColumn.options(
				inspectionValueList, true,
				CommonCodeColumn.change(updateTQC5010_R01List), handleGridDropDownCommitAfter,
			),
		},
		{
			headerName: '검사방법', colId: 'inspMethod', field: 'inspMethod', width: 90,
			...CommonCodeColumn.options(
				inspectMethodList, true,
				CommonCodeColumn.change(updateTQC5010_R01List), handleGridDropDownCommitAfter,
			),
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 200,
			editable: true,
			pasteable: true,
		},
	], [
		
		renderIndexCellHandler,
		itemGroupList, inspectionTypeList, inspectionRangeList, inspectionValueList, inspectMethodList, unitList,
		updateTQC5010_R01List, handleGridDropDownCommitAfter, createCellClassRules,
	]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<TQC5010_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<TQC5010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState]);

	const handleCellChange = useCellChangeHandler({
		dataList: TQC5010_R01List,
		originalDataList: originalTQC5010_R01List,
		updateRow: updateTQC5010_R01List,
	});

	const cellClickActions: CustomCellClickAction<TQC5010_R01Dto>[] = useMemo(() => [{
		validate: (field) => defaultEditableColumns.includes(field),
		action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); },
	}], []);

	const handleCellClick = CreateCellClickHandler(TQC5010_R01List, 'valueIndex', cellClickActions);

	const cellEventHandlers = useDetailTableEvents({
		detailList: TQC5010_R01List,
		editableColumns: defaultEditableColumns,
		keyDownConfig: { enableArrowNavigation: true },
	});

	const handlePostSort = useCallback((params: PostSortRowsParams<TQC5010_R01Dto>) => {
		postSortHandler(params, TQC5010_R01List, originalTQC5010_R01List, setTQC5010_R01List);
	}, [TQC5010_R01List, originalTQC5010_R01List, setTQC5010_R01List]);

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		idKey: 'valueIndex' as const,
		selectedIds: selectedTQC5010_R01List,
		setSelectedIds: (ids: Array<string | number>) => setSelectedTQC5010_R01List(ids as any),
		isRowSelectable: (event: RowClickedEvent<TQC5010_R01Dto>) => {
			if (!clickable) {
				setClickable(true);
				return false;
			}
			return !!event.data;
		},
	}), [selectedTQC5010_R01List, setSelectedTQC5010_R01List, clickable, setClickable]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '계',
		inspItemGroup: `${TQC5010_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [TQC5010_R01List]);

	return (
		<GridContextWrapper<TQC5010_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setTQC5010_R01List}
			initSelected={initSelectedTQC5010_R01List}
			defaultRowData={defaultTQC5010_R01Row}
			allSelected={allSelectedTQC5010_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<TQC5010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				rowClassRules={createRowClassRules<TQC5010_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedTQC5010_R01List,
					deletedList: deletedTQC5010_R01List,
				})}
				shiftRangeSelection={shiftRangeSelectionConfig}
				onCellClicked={handleCellClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}

				onPostSortRows={handlePostSort}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
